import type { AdminDeviceConnectionEvent, AdminLifeSignalDetail, AdminLifeSignalItem, AdminLifeSignalSummary, ApiEnvelope, DashboardDeviceIssue, DashboardSummary, DeviceDetail, DeviceItem, DeviceStatusLogItem, ElderlyUserDetail, ElderlyUserDeviceItem, ElderlyUserEmergencyEventItem, ElderlyUserFamilyLinkItem, ElderlyUserItem, EmergencyEventDetail, EmergencyEventItem, LoginResponse, MeResponse, MemberDetail, MemberItem, NotificationLogItem, SystemVersionResponse, } from "@/types.ts";
import type { AdminDailyScheduleItem, AdminHealthHomeSummary } from "@/types.ts";
import type { AdminCustomerInquiryDetail, AdminCustomerInquiryItem } from "@/types.ts";
import type { AdminFamilyConnectionDetail, AdminFamilyConnectionItem, AdminSeniorFamilyConnectionDetail, AdminSeniorFamilyConnectionSummary } from "@/types.ts";
import type { AdminLocationDetail, AdminLocationItem } from "@/types.ts";
import type { AdminTermsPolicyDetail, AdminTermsPolicyItem, AdminTermsPolicySaveRequest, PublicTermsDetail, PublicTermsListItem } from "@/types.ts";
import type { CommonCodeOption } from "@/types.ts";
import type { SoundModelLabel, SoundModelMappingRule, SoundModelRelease, SoundModelReleaseMutationResponse, SoundModelReleaseSaveRequest } from "@/types.ts";
import { membershipQuery, type MembershipTab, type MembershipFilters, type MembershipRow, type MembershipPage, type MembershipPlan, type MembershipDetail } from "./membership";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8081";
export const fetchAdminMemberships = async (tab: MembershipTab, filters: MembershipFilters, page: number): Promise<MembershipPage<MembershipRow>> => {
    if (tab === "plans") {
        const result = await request<{ items: MembershipPlan[] }>("/api/v1/admin/memberships/plans");
        return { items: result.items, totalElements: result.items.length, page: 0, size: result.items.length, totalPages: 1 };
    }
    const path = tab === "orders" || tab === "tokens" ? "transactions" : tab;
    return request<MembershipPage<MembershipRow>>(`/api/v1/admin/memberships/${path}?${membershipQuery(tab, filters, page)}`);
};
export const fetchAdminMembershipDetail = (id: number) =>
    request<MembershipDetail>(`/api/v1/admin/memberships/transactions/${id}`);
const ACCESS_TOKEN_KEY = "daeul_admin_access_token";
const REFRESH_TOKEN_KEY = "daeul_admin_refresh_token";

type ApiRequestInit = NonNullable<Parameters<typeof fetch>[1]> & {
    auth?: boolean;
};

export class ApiError extends Error {
    status: number;
    code?: string;

    constructor(message: string, status: number, code?: string) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.code = code;
    }
}

const getStatusFallbackMessage = (status: number) => {
    if (status === 401)
        return "로그인이 만료되었거나 인증 정보가 없습니다. 다시 로그인해 주세요.";
    if (status === 403)
        return "접근 권한이 없습니다.";
    if (status === 404)
        return "요청한 데이터를 찾을 수 없습니다.";
    if (status >= 500)
        return "서버 처리 중 오류가 발생했습니다.";
    return "요청 처리 중 오류가 발생했습니다.";
};

const isUnreadableMessage = (message?: string | null) => {
    if (!message)
        return true;
    return /^\?[\?\s.]+$/.test(message.trim());
};

const getStoredAccessToken = () => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

type TokenRefreshResponse = Pick<LoginResponse, "accessToken" | "refreshToken" | "tokenType" | "expiresIn">;

type AdminLoginApiResponse = Omit<LoginResponse, "member"> & {
    member: {
        memberId: number;
        loginId: string;
        name: string;
        roleType: string;
    };
};

type AdminMeApiResponse = Omit<MeResponse, "adminLoginAccountId"> & {
    memberId: number;
};

let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
    if (refreshPromise) return refreshPromise;

    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
        throw new ApiError(getStatusFallbackMessage(401), 401, "AUTH_REQUIRED");
    }

    refreshPromise = (async () => {
        const response = await fetch(`${API_BASE_URL}/api/v1/admin/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
        });
        const payload = (await response.json().catch(() => null)) as ApiEnvelope<TokenRefreshResponse> | null;
        if (!response.ok || !payload?.success || !payload.data?.accessToken || !payload.data?.refreshToken) {
            const fallbackMessage = getStatusFallbackMessage(response.status || 401);
            const message = isUnreadableMessage(payload?.message) ? fallbackMessage : payload?.message ?? fallbackMessage;
            throw new ApiError(message, response.status || 401, payload?.code);
        }
        localStorage.setItem(ACCESS_TOKEN_KEY, payload.data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, payload.data.refreshToken);
        return payload.data.accessToken;
    })().finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};

const redirectToAdminLogin = () => {
    if (typeof window === "undefined") {
        return;
    }
    const loginPath = "/admin/login";
    if (window.location.pathname === loginPath) {
        return;
    }
    window.location.replace(loginPath);
};

const request = async <T>(path: string, init?: ApiRequestInit): Promise<T> => {
    const { auth = true, ...requestInit } = init ?? {};
    let token = auth ? getStoredAccessToken() : null;
    if (auth && !token) {
        clearSession();
        redirectToAdminLogin();
        throw new ApiError(getStatusFallbackMessage(401), 401, "AUTH_REQUIRED");
    }
    const send = async (accessToken: string | null) => {
        const response = await fetch(`${API_BASE_URL}${path}`, {
            ...requestInit,
            headers: {
                "Content-Type": "application/json",
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                ...requestInit.headers,
            },
        });
        const payload = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
        return { response, payload };
    };

    let { response, payload } = await send(token);
    if (auth && response.status === 401) {
        try {
            token = await refreshAccessToken();
            ({ response, payload } = await send(token));
        } catch (error) {
            clearSession();
            redirectToAdminLogin();
            throw error;
        }
    }
    if (!response.ok || !payload?.success) {
        if (auth && response.status === 401) {
            clearSession();
            redirectToAdminLogin();
        }
        const fallbackMessage = getStatusFallbackMessage(response.status);
        const message = isUnreadableMessage(payload?.message) ? fallbackMessage : payload?.message ?? fallbackMessage;
        throw new ApiError(message, response.status, payload?.code);
    }
    return payload.data;
};
export const getStoredToken = () => {
    return getStoredAccessToken();
};
export const clearSession = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
};
export const login = async (loginId: string, password: string) => {
    clearSession();
    const data = await request<AdminLoginApiResponse>("/api/v1/admin/auth/login", {
        auth: false,
        method: "POST",
        body: JSON.stringify({ loginId, password }),
    });
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    return {
        ...data,
        member: {
            adminLoginAccountId: data.member.memberId,
            loginId: data.member.loginId,
            name: data.member.name,
            roleType: data.member.roleType,
        },
    } satisfies LoginResponse;
};
export const fetchMe = async () => {
    const data = await request<AdminMeApiResponse>("/api/v1/admin/auth/me");
    return {
        adminLoginAccountId: data.memberId,
        loginId: data.loginId,
        name: data.name,
        roleType: data.roleType,
        status: data.status,
    } satisfies MeResponse;
};
export const fetchSystemVersion = async () => {
    return request<SystemVersionResponse>("/api/v1/system/version", { auth: false });
};
export const fetchDashboardSummary = async () => {
    return request<DashboardSummary>("/api/v1/admin/dashboard/summary");
};
type EmergencyEventApiItem = EmergencyEventItem;
type EmergencyEventApiDetail = EmergencyEventDetail;
const mapEmergencyEventItem = (event: EmergencyEventApiItem): EmergencyEventItem => {
    return {
        eventId: event.eventId,
        seniorUserAccountId: event.seniorUserAccountId,
        elderlyName: event.elderlyName,
        deviceId: event.deviceId ?? null,
        eventType: event.eventType,
        eventTypeName: event.eventTypeName ?? null,
        eventTypeShortName: event.eventTypeShortName ?? null,
        eventTypeDescription: event.eventTypeDescription ?? null,
        eventTypeSeverity: event.eventTypeSeverity ?? null,
        riskLevel: event.riskLevel,
        eventStatus: event.eventStatus,
        eventStatusName: event.eventStatusName ?? null,
        eventStatusShortName: event.eventStatusShortName ?? null,
        eventStatusDescription: event.eventStatusDescription ?? null,
        eventSeverity: event.eventSeverity ?? null,
        description: event.description ?? null,
        occurredAt: event.occurredAt,
    };
};
const mapEmergencyEventDetail = (event: EmergencyEventApiDetail): EmergencyEventDetail => {
    return {
        ...mapEmergencyEventItem(event),
        resolvedAt: event.resolvedAt ?? null,
    };
};
export const fetchRecentEmergencyEvents = async (params?: { seniorUserAccountId?: number }) => {
    const data = await request<{
        items: EmergencyEventApiItem[];
    }>(`/api/v1/admin/emergency-events${toQueryString(params)}`);
    return data.items.map(mapEmergencyEventItem);
};
export const fetchDashboardRecentEmergencyEvents = async (limit = 8) => {
    const data = await request<EmergencyEventApiItem[]>(`/api/v1/admin/dashboard/recent-emergency-events?limit=${limit}`);
    return data.map((event) => ({
        ...mapEmergencyEventItem(event),
        deviceId: event.deviceId ?? null,
        description: event.description ?? null,
    }));
};
export const fetchMembers = async () => {
    const data = await request<{
        items: MemberItem[];
    }>("/api/v1/admin/members");
    return data.items;
};
export const fetchMemberDetail = async (accountId: number) => {
    return request<MemberDetail>(`/api/v1/admin/members/${accountId}`);
};

export const fetchDashboardRecentDeviceIssues = (limit = 5) =>
    request<DashboardDeviceIssue[]>(`/api/v1/admin/dashboard/recent-device-issues?limit=${limit}`);
export type AdminMutationAuditResponse = {
    auditLogId: number;
    actionCode: string;
    targetType: string;
    targetId: string;
    beforeStatus: string;
    afterStatus: string;
    performedAt: string;
    requestId: string;
};
export type AdminActionAuditLogItem = {
    auditLogId: number;
    actorAdminLoginAccountId: number;
    actorLoginId: string | null;
    actorName: string | null;
    actorRoleCode: string;
    actionCode: string;
    targetType: string;
    targetId: string;
    reasonCode: string;
    reason: string;
    beforeSummary: string | null;
    afterSummary: string | null;
    resultCode: string;
    errorCode: string | null;
    requestId: string;
    performedAt: string;
};
export const fetchAdminActionAuditLogs = async (params?: {
    actionCode?: string;
    targetType?: string;
    targetId?: string;
    reasonCode?: string;
    resultCode?: string;
    requestId?: string;
    page?: number;
    size?: number;
}) => request<{ items: AdminActionAuditLogItem[]; total: number; page: number; size: number; totalPages: number }>(
    `/api/v1/admin/audit-logs${toQueryString(params)}`,
);
export type AdminMutationImpactPreview = {
    targetType: string;
    targetId: string;
    targetLabel: string;
    executable: boolean;
    blockers: string[];
    impacts: Array<{ code: string; label: string; count: number }>;
};
export type AdminDestructiveMutationRequest = {
    reasonCode: string;
    reason: string;
    confirmationText: string;
};
export type AdminDestructiveMutationResponse = {
    targetType: string;
    targetId: string;
    resultStatus: string;
    audit: AdminMutationAuditResponse;
};
export const fetchMemberDeletionPreview = async (accountId: number) =>
    request<AdminMutationImpactPreview>(`/api/v1/admin/members/${accountId}/deletion-preview`);
export type MemberStatusUpdateResponse = {
    userAccountId: number;
    status: string;
    updatedAt: string;
    audit: AdminMutationAuditResponse;
};
export const updateMemberStatus = async (accountId: number, status: string, reasonCode: string, reason: string) => {
    return request<MemberStatusUpdateResponse>(`/api/v1/admin/members/${accountId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status, reasonCode, reason }),
    });
};
export const deleteMemberAccount = async (accountId: number, payload: AdminDestructiveMutationRequest) => {
    return request<AdminDestructiveMutationResponse>(`/api/v1/admin/members/${accountId}/delete`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
};
type SeniorAccountApiItem = Omit<ElderlyUserItem, "userAccountId"> & {
    memberId: number | null;
};
type SeniorAccountApiDetail = Omit<ElderlyUserDetail, "userAccountId"> & {
    memberId: number | null;
};
const mapSeniorAccountToElderlyUser = (item: SeniorAccountApiItem): ElderlyUserItem => {
    return {
        seniorUserAccountId: item.seniorUserAccountId,
        userAccountId: item.seniorUserAccountId,
        name: item.name,
        birthDate: item.birthDate ?? null,
        gender: item.gender,
        deviceCount: item.deviceCount,
        familyLinkCount: item.familyLinkCount,
    };
};
const mapSeniorAccountToElderlyUserDetail = (item: SeniorAccountApiDetail): ElderlyUserDetail => {
    return {
        seniorUserAccountId: item.seniorUserAccountId,
        userAccountId: item.seniorUserAccountId,
        name: item.name,
        birthDate: item.birthDate ?? null,
        gender: item.gender,
        address: item.address ?? null,
        memo: item.memo ?? null,
    };
};
export const fetchElderlyUsers = async () => {
    const data = await request<{
        items: SeniorAccountApiItem[];
    }>("/api/v1/admin/senior-accounts");
    return data.items.map(mapSeniorAccountToElderlyUser);
};
export const fetchElderlyUserDetail = async (seniorUserAccountId: number) => {
    const data = await request<SeniorAccountApiDetail>(`/api/v1/admin/senior-accounts/${seniorUserAccountId}`);
    return mapSeniorAccountToElderlyUserDetail(data);
};
export const fetchElderlyUserDevices = async (seniorUserAccountId: number) => {
    const data = await request<{
        items: ElderlyUserDeviceItem[];
    }>(`/api/v1/admin/senior-accounts/${seniorUserAccountId}/devices`);
    return data.items;
};
export const fetchElderlyUserFamilyLinks = async (seniorUserAccountId: number) => {
    const data = await request<{
        items: ElderlyUserFamilyLinkItem[];
    }>(`/api/v1/admin/senior-accounts/${seniorUserAccountId}/family-links`);
    return data.items;
};
export const fetchElderlyUserEmergencyEvents = async (seniorUserAccountId: number) => {
    const data = await request<{
        items: ElderlyUserEmergencyEventItem[];
    }>(`/api/v1/admin/senior-accounts/${seniorUserAccountId}/emergency-events`);
    return data.items;
};
export const fetchSeniorHealthHomeSummary = async (seniorUserAccountId: number, date: string) => {
    return request<AdminHealthHomeSummary>(`/api/v1/admin/senior-accounts/${seniorUserAccountId}/health/home-summary${toQueryString({ date })}`);
};
export const fetchAdminSeniorFamilyConnectionSummaries = async () => {
    const data = await request<{
        items: AdminSeniorFamilyConnectionSummary[];
    }>("/api/v1/admin/family-connections/seniors");
    return data.items;
};
export const fetchAdminFamilyConnections = async () => {
    const data = await request<{ items: AdminFamilyConnectionItem[] }>("/api/v1/admin/family-connections");
    return data.items;
};
export const fetchAdminFamilyConnectionDetail = (connectionId: number) =>
    request<AdminFamilyConnectionDetail>(`/api/v1/admin/family-connections/${connectionId}`);
export const fetchAdminSeniorFamilyConnectionDetail = async (seniorUserAccountId: number) => {
    return request<AdminSeniorFamilyConnectionDetail>(`/api/v1/admin/family-connections/seniors/${seniorUserAccountId}`);
};
export const disconnectAdminFamilyConnection = async (connectionId: number, payload: AdminDestructiveMutationRequest) => {
    return request<AdminDestructiveMutationResponse>(`/api/v1/admin/family-connections/${connectionId}/disconnect-command`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
};
type AdminDailyScheduleApiItem = Omit<AdminDailyScheduleItem, "regDt" | "modDt"> & {
    registeredAt: string;
    updatedAt: string;
};

export const fetchSeniorConnectionEvents = async (seniorUserAccountId: number, size = 50) => {
    const data = await request<{ items: AdminDeviceConnectionEvent[] }>(
        `/api/v1/admin/senior-accounts/${seniorUserAccountId}/connection-events${toQueryString({ size })}`,
    );
    return data.items;
};
export const fetchAdminFamilyDisconnectPreview = async (connectionId: number) =>
    request<AdminMutationImpactPreview>(`/api/v1/admin/family-connections/${connectionId}/disconnect-preview`);
const mapDailyScheduleItem = (item: AdminDailyScheduleApiItem): AdminDailyScheduleItem => ({
    ...item,
    elderlyDailyScheduleId: item.elderlyDailyScheduleId,
    seniorUserAccountId: item.seniorUserAccountId,
    elderlyName: item.elderlyName,
    guardianName: item.guardianName ?? null,
    scheduleType: item.scheduleType,
    scheduleTypeName: item.scheduleTypeName,
    title: item.title,
    repeatType: item.repeatType,
    scheduleDate: item.scheduleDate ?? null,
    scheduleTime: item.scheduleTime,
    repeatDays: item.repeatDays,
    repeatLabel: item.repeatLabel,
    noResponseRealert: item.noResponseRealert,
    completeCheck: item.completeCheck,
    retryCount: item.retryCount,
    retryIntervalMin: item.retryIntervalMin,
    active: item.active,
    regDt: item.registeredAt,
    modDt: item.updatedAt,
});
export const fetchAdminDailySchedules = async (params?: {
    keyword?: string;
    scheduleType?: string;
    active?: boolean | null;
}) => {
    const data = await request<{
        items: AdminDailyScheduleApiItem[];
    }>(`/api/v1/admin/daily-schedules${toQueryString({
        keyword: params?.keyword,
        scheduleType: params?.scheduleType,
        active: params?.active == null ? undefined : String(params.active),
    })}`);
    return data.items.map(mapDailyScheduleItem);
};
export const updateAdminDailyScheduleActive = async (scheduleId: number, active: boolean, reasonCode: string, reason: string) => {
    const data = await request<{ schedule: AdminDailyScheduleApiItem; audit: AdminMutationAuditResponse }>(`/api/v1/admin/daily-schedules/${scheduleId}/active`, {
        method: "PATCH",
        body: JSON.stringify({ active, reasonCode, reason }),
    });
    return { schedule: mapDailyScheduleItem(data.schedule), audit: data.audit };
};
export const fetchDevices = async (params?: { seniorUserAccountId?: number; status?: string; keyword?: string }) => {
    type DeviceApiItem = DeviceItem;
    const mapDeviceItem = (item: DeviceApiItem): DeviceItem => ({
        ...item,
        deviceId: item.deviceId,
        seniorUserAccountId: item.seniorUserAccountId,
        elderlyName: item.elderlyName,
        deviceUuid: item.deviceUuid,
        deviceName: item.deviceName ?? null,
        platformType: item.platformType,
        status: item.status,
        appVersion: item.appVersion ?? null,
        lastOnlineAt: item.lastOnlineAt ?? null,
    });
    const data = await request<{
        items: DeviceApiItem[];
    }>(`/api/v1/admin/devices${toQueryString(params)}`);
    return data.items.map(mapDeviceItem);
};
export const fetchEmergencyEventDetail = async (eventId: number) => {
    const data = await request<EmergencyEventApiDetail>(`/api/v1/admin/emergency-events/${eventId}`);
    return mapEmergencyEventDetail(data);
};
export const fetchEmergencyEventNotificationLogs = async (eventId: number) => {
    type NotificationLogApiItem = Omit<NotificationLogItem, "recipientUserAccountId"> & {
        memberId: number | null;
    };
    const data = await request<{
        items: NotificationLogApiItem[];
    }>(`/api/v1/admin/emergency-events/${eventId}/notification-logs`);
    return data.items.map(({ memberId, ...item }) => ({
        ...item,
        recipientUserAccountId: memberId,
    }));
};
export type AdminEmergencyLifecycle = {
    incident: {
        eventId: number;
        eventStatus: string;
        acknowledgedByUserAccountId: number | null;
        acknowledgedAt: string | null;
        respondingUserAccountId: number | null;
        respondingUserName: string | null;
        responseStartedAt: string | null;
        resolvedByUserAccountId: number | null;
        resolvedAt: string | null;
        resolutionCode: string | null;
        versionNo: number;
    };
    availableActions: string[];
    historyComplete: boolean;
};
export const fetchAdminEmergencyLifecycle = async (eventId: number) =>
    request<AdminEmergencyLifecycle>(`/api/v1/admin/emergency-events/${eventId}/lifecycle`);
export const reopenAdminEmergencyLifecycle = async (eventId: number, expectedVersionNo: number, reasonCode: string, reason: string) =>
    request<{ lifecycle: AdminEmergencyLifecycle; audit: AdminMutationAuditResponse }>(`/api/v1/admin/emergency-events/${eventId}/lifecycle/reopen`, {
        method: "POST",
        body: JSON.stringify({ clientRequestId: crypto.randomUUID(), expectedVersionNo, reasonCode, reason }),
    });
type DeviceApiDetail = DeviceDetail;

const mapDeviceDetail = (item: DeviceApiDetail): DeviceDetail => ({
    ...item,
    deviceId: item.deviceId,
    seniorUserAccountId: item.seniorUserAccountId,
    elderlyName: item.elderlyName,
    deviceUuid: item.deviceUuid,
    deviceName: item.deviceName ?? null,
    platformType: item.platformType,
    status: item.status,
    appVersion: item.appVersion ?? null,
    lastOnlineAt: item.lastOnlineAt ?? null,
    createdAt: item.createdAt,
});

export const fetchDeviceDetail = async (deviceId: number) => {
    const data = await request<DeviceApiDetail>(`/api/v1/admin/devices/${deviceId}`);
    return mapDeviceDetail(data);
};
export const fetchDeviceStatusLogs = async (deviceId: number) => {
    const data = await request<{
        items: DeviceStatusLogItem[];
    }>(`/api/v1/admin/devices/${deviceId}/status-logs`);
    return data.items;
};
export const fetchAdminLocations = async (params?: {
    keyword?: string;
    status?: string;
}) => {
    type AdminLocationApiItem = Omit<AdminLocationItem, "seniorUserAccountId"> & {
        subjectUserAccountId: number;
    };
    const mapLocationItem = (item: AdminLocationApiItem): AdminLocationItem => ({
        seniorUserAccountId: item.subjectUserAccountId,
        elderlyName: item.elderlyName,
        deviceId: item.deviceId ?? null,
        deviceName: item.deviceName ?? null,
        locationLogId: item.locationLogId ?? null,
        source: item.source ?? null,
        latitude: item.latitude ?? null,
        longitude: item.longitude ?? null,
        accuracyMeter: item.accuracyMeter ?? null,
        addressText: item.addressText ?? null,
        addressDisplayLabel: item.addressDisplayLabel ?? null,
        sido: item.sido ?? null,
        sigungu: item.sigungu ?? null,
        permissionStatus: item.permissionStatus ?? null,
        providerStatus: item.providerStatus ?? null,
        serviceEnabled: item.serviceEnabled ?? null,
        collectedAt: item.collectedAt ?? null,
        lastCheckedAt: item.lastCheckedAt ?? null,
    });
    const data = await request<{
        items: AdminLocationApiItem[];
    }>(`/api/v1/admin/locations${toQueryString(params)}`);
    return data.items.map(mapLocationItem);
};
export const fetchAdminLocationDetail = async (seniorUserAccountId: number) => {
    type AdminLocationApiDetail = Omit<AdminLocationDetail, "seniorUserAccountId" | "latestLocation"> & {
        subjectUserAccountId: number;
        latestLocation: (Omit<AdminLocationItem, "seniorUserAccountId"> & {
            subjectUserAccountId: number;
        }) | null;
    };
    const mapDetailLocationItem = (item: NonNullable<AdminLocationApiDetail["latestLocation"]>): AdminLocationItem => ({
        seniorUserAccountId: item.subjectUserAccountId,
        elderlyName: item.elderlyName,
        deviceId: item.deviceId ?? null,
        deviceName: item.deviceName ?? null,
        locationLogId: item.locationLogId ?? null,
        source: item.source ?? null,
        latitude: item.latitude ?? null,
        longitude: item.longitude ?? null,
        accuracyMeter: item.accuracyMeter ?? null,
        addressText: item.addressText ?? null,
        addressDisplayLabel: item.addressDisplayLabel ?? null,
        sido: item.sido ?? null,
        sigungu: item.sigungu ?? null,
        permissionStatus: item.permissionStatus ?? null,
        providerStatus: item.providerStatus ?? null,
        serviceEnabled: item.serviceEnabled ?? null,
        collectedAt: item.collectedAt ?? null,
        lastCheckedAt: item.lastCheckedAt ?? null,
    });
    const data = await request<AdminLocationApiDetail>(`/api/v1/admin/locations/senior-accounts/${seniorUserAccountId}`);
    return {
        seniorUserAccountId: data.subjectUserAccountId,
        elderlyName: data.elderlyName,
        latestLocation: data.latestLocation ? mapDetailLocationItem(data.latestLocation) : null,
        history: data.history,
        importantPlaces: data.importantPlaces,
    };
};
export const updateDeviceStatus = async (deviceId: number, status: string, reasonCode: string, reason: string) => {
    return request<{ audit: AdminMutationAuditResponse }>(`/api/v1/admin/devices/${deviceId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status, reasonCode, reason }),
    });
};
type SoundAnalysisQuery = {
    subjectKey?: string;
    deviceId?: number;
    soundType?: string;
    eventStatusCode?: "SAVED" | "DIAGNOSTIC" | "SUPERSEDED";
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
};
export type PagedResponse<T> = {
    items: T[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
};
export type SoundAnalysisV2ListItem = {
    soundAnalysisEventId: number;
    subjectKey: string;
    seniorUserAccountId: number | null;
    seniorName: string | null;
    deviceId: number | null;
    deviceName: string | null;
    eventStatusCode: string;
    diagnosticReasonCode: string | null;
    candidateContractVersion: string;
    representativeGroupCode: string | null;
    representativeLabelCode: string | null;
    representativeDisplayLabel: string | null;
    representativeModelLabelCode: string | null;
    representativeConfidence: number | null;
    riskLevelCode: string | null;
    qualityStatusCode: string;
    candidateCount: number;
    analyzedAt: string;
};
export type SoundAnalysisV2Detail = SoundAnalysisV2ListItem & {
    riskLevelCode: string | null;
    modelName: string | null;
    modelVersion: string | null;
    modelLabelVersion: string | null;
    taxonomyVersion: string | null;
    activationPolicyVersion: string | null;
    purposeCode: string | null;
    candidates: Array<{
        soundAnalysisEventCandidateId: number;
        rank: number;
        labelIndex: number;
        modelLabelCode: string;
        score: number | null;
        mappingSnapshotStatusCode: string | null;
        soundGroupCode: string | null;
        displayLabelCode: string | null;
        displayLabelKo: string | null;
        riskLevelCode: string | null;
        operationalThreshold: number | null;
        operationalEligibleYn: string | null;
    }>;
    metric: Record<string, number | string | null> | null;
};
export type AdminSoundStatisticsQuery = {
    fromDate?: string;
    toDate?: string;
    modelVersion?: string;
    taxonomyVersion?: string;
    activationPolicyVersion?: string;
    scheduleProfileCode?: string;
};
export type AdminSoundStatisticsSummary = {
    fromDate: string;
    toDate: string;
    dateBasis: string;
    operationalEventCount: number;
    diagnosticEventCount: number;
    lowConfidenceDiagnosticCount: number;
    unmappedDiagnosticCount: number;
    silenceDiagnosticCount: number;
    nonStandardProfileDiagnosticCount: number;
    unspecifiedDiagnosticCount: number;
    uniqueDisplayCandidateObservationCount: number;
    duplicateDisplayLabelPolicy: string;
};
export type AdminSoundLabelStatistics = {
    soundGroupCode: string;
    displayLabelCode: string;
    displayLabel: string;
    riskLevelCode: string;
    operating: boolean;
    candidateObservationCount: number;
    averageScoreApprox: number | null;
    masked: boolean;
};
export type AdminSoundConfidenceBin = {
    scoreFromInclusive: number;
    scoreToExclusive: number;
    candidateObservationCount: number;
    masked: boolean;
};
export type AdminSoundThresholdPolicy = {
    policyVersion: string;
    policyStatusCode: string;
    candidateFloor: number | null;
    candidateRelativeRatio: number | null;
    effectiveFromDt: string | null;
    effectiveToDt: string | null;
    changeReason: string | null;
    evidenceReference: string | null;
    rules: Array<{
        displayLabelCode: string;
        displayLabel: string;
        soundGroupCode: string;
        riskLevelCode: string;
        minimumThreshold: number | null;
        maximumThreshold: number | null;
        yamnetLabelCount: number;
        mappingStatus: string;
        useYn: string;
    }>;
};
export type AdminSoundThresholdSimulationRequest = AdminSoundStatisticsQuery & {
    fromDate: string;
    toDate: string;
    noneThreshold: number;
    contextThreshold: number;
    attentionThreshold: number;
    highThreshold: number;
    displayLabelThresholds?: Record<string, number>;
};
export type AdminSoundThresholdSimulationResult = {
    fromDate: string;
    toDate: string;
    simulationMode: string;
    activationEffect: string;
    sampleCount: number;
    currentEligibleCount: number;
    proposedEligibleCount: number;
    eligibleDelta: number;
    nonOperatingObservationCount: number;
    note: string | null;
};
export type SoundAnalysisTuningSampleCreateRequest = {
    sampleName?: string | null;
    testScenario?: string | null;
    playbackSource?: string | null;
    expectedSoundGroupCode: string;
    expectedDisplayLabelCode: string;
    expectedRiskYn?: string | null;
    correctYn?: string | null;
    playbackVolumeLevel?: number | null;
    distanceCm?: number | null;
    microphonePosition?: string | null;
    memo?: string | null;
};
export type SoundAnalysisTuningSample = SoundAnalysisTuningSampleCreateRequest & {
    soundAnalysisTuningSampleId: number;
    soundAnalysisEventId: number;
    soundAnalysisEventCandidateId: number;
    soundModelLabelId: number | null;
    actualSoundGroupCode: string | null;
    actualDisplayLabelCode: string | null;
    actualModelName: string | null;
    actualModelVersion: string | null;
    actualModelLabelVersion: string | null;
    actualModelLabelCode: string | null;
    actualModelLabel: string | null;
    actualModelConfidence: number | null;
    diagnosticReasonCode: string | null;
    reviewerAccountId: number | null;
    reviewedAt: string | null;
    regDt: string | null;
};
export type AdminEafrEventQuery = {
    analysisStatusCode?: "COMPLETE" | "PARTIAL" | "FAILED";
    resultAvailableYn?: "Y" | "N";
    displayStateCode?: "COMPLETE" | "PARTIAL" | "FAILED";
    needsAttentionYn?: "Y" | "N";
    soundModelReleaseId?: number;
    deviceId?: number;
    seniorUserAccountId?: number;
    primarySituationCode?: string;
    artifactCompletionStatusCode?: "NONE" | "PENDING" | "COMPLETE" | "PARTIAL" | "FAILED";
    rawAudioStatusCode?: "VERIFIED" | "STORAGE_MISSING" | "STORAGE_CORRUPTED" | "FAILED" | "DELETED" | "EXPIRED";
    page?: number;
    size?: number;
};
export type AdminEafrEventListItem = {
    soundAnalysisEventId: number;
    seniorUserAccountId: number | null;
    seniorDisplayName: string | null;
    deviceId: number | null;
    deviceName: string | null;
    analysisGenerationCode: string;
    analysisContractVersion: string;
    soundModelReleaseId: number | null;
    analysisBundleVersion: string | null;
    analysisStatusCode: string;
    resultAvailableYn: string;
    displayStateCode: string;
    limitationCodes: string[];
    analysisDurationMs: number | null;
    completedComponentCount: number;
    partialComponentCount: number;
    failedComponentCount: number;
    skippedComponentCount: number;
    primarySituationCode: string | null;
    primarySituationScore: number | null;
    needsAttentionYn: string;
    semanticSafetyDetectedYn: string;
    artifactCompletionStatusCode: string;
    artifactRequiredCount: number;
    artifactUploadedCount: number;
    rawAudioStatusCode: string | null;
    validationStatusCode: string;
    captureStartedAt: string | null;
    captureEndedAt: string | null;
    analyzedAt: string | null;
    receivedAt: string | null;
    collectionPolicyVersion: number | null;
    scheduleRuleId: number | null;
    scheduleProfileCodes: string[];
};
export type AdminEafrEventDetail = {
    event: AdminEafrEventListItem;
    bundle: Record<string, string | number | null> | null;
    componentStatuses: Array<Record<string, string | number | null>>;
    quality: Record<string, string | number | string[] | null> | null;
    predictions: Array<{
        predictionDomainCode: string; rankNo: number; modelLabelCode: string;
        derivedLabelCode: string | null; rawScore: number | null; filteredScore: number | null;
        operationalEligibleYn: string; filterStatusCode: string; filterReasonCodes: string[];
    }>;
    situations: Array<{
        situationId: number; rankNo: number; situationCode: string; score: number;
        reliableYn: string; needsAttentionYn: string; policyVersion: string;
        limitationCodes: string[]; evidence: Array<{
            sequenceNo: number; componentCode: string; evidenceCode: string; score: number | null;
            frameFrom: number | null; frameTo: number | null; fromMs: number | null; toMs: number | null;
            evidenceReferenceCode: string | null;
        }>;
    }>;
    livingSignals: Array<{
        sequenceNo: number; livingAxisCode: string; signalCode: string; score: number;
        reliableYn: string; sourceSituationCodes: string[]; evidenceReferenceCodes: string[];
        livingPolicyVersion: string;
    }>;
    speakerSummary: Record<string, string | number | null> | null;
    speechSource: Record<string, string | number | null> | null;
    asrSummary: Record<string, unknown> | null;
    emotion: Record<string, unknown> | null;
    artifacts: Array<{
        artifactId: number; artifactClientKey: string; artifactCode: string; componentCode: string;
        schemaVersion: string; encodingCode: string; compressionCode: string;
        uncompressedSizeByte: number; compressedSizeByte: number; sha256Checksum: string;
        lifecycleStatusCode: string; retentionUntil: string | null;
    }>;
    rawAudio: Record<string, string | number | null> | null;
    currentValidation: Record<string, string | number | null> | null;
};
export type AdminSoundValidationResultCode = "CONFIRMED" | "CORRECTED" | "FALSE_POSITIVE" | "UNDETERMINED";
export type AdminSoundValidationFilter = "UNREVIEWED" | AdminSoundValidationResultCode;
export type AdminSoundUserDisplayActionCode = "KEEP_ORIGINAL" | "SHOW_CORRECTED" | "HIDE";
export type AdminSoundRawAudioReviewItem = {
    soundAnalysisEventId: number;
    analyzedAt: string;
    eventStatusCode: string;
    qualityStatusCode: string;
    representativeGroupCode: string | null;
    representativeLabelCode: string | null;
    representativeDisplayLabel: string | null;
    representativeConfidence: number | null;
    modelName: string | null;
    modelVersion: string | null;
    candidateCount: number;
    rawAudioDurationMs: number;
    rawAudioFileSizeBytes: number;
    validationResultCode: AdminSoundValidationResultCode | null;
    userDisplayActionCode: AdminSoundUserDisplayActionCode | null;
    validatedAt: string | null;
};
export type AdminSoundRawAudioCandidate = {
    soundAnalysisEventCandidateId: number;
    rank: number;
    labelIndex: number;
    modelLabelCode: string;
    score: number | null;
    mappingSnapshotStatusCode: string | null;
    soundGroupCode: string | null;
    displayLabelCode: string | null;
    displayLabel: string | null;
    riskLevelCode: string | null;
    operationalThreshold: number | null;
    operationalEligibleYn: string | null;
};
export type AdminSoundRawAudioValidation = {
    validationResultCode: AdminSoundValidationResultCode;
    userDisplayActionCode: AdminSoundUserDisplayActionCode;
    correctedSoundGroupCode: string | null;
    correctedDisplayLabelCode: string | null;
    selectedCandidateId: number | null;
    validationReason: string;
    validatedAt: string;
};
export type AdminSoundRawAudioValidationHistory = AdminSoundRawAudioValidation & { version: number };
export type AdminSoundRawAudioReviewDetail = AdminSoundRawAudioReviewItem & {
    candidateContractVersion: string;
    diagnosticReasonCode: string | null;
    riskLevelCode: string | null;
    modelLabelVersion: string | null;
    taxonomyVersion: string | null;
    activationPolicyVersion: string | null;
    purposeCode: string;
    rawAudio: {
        lifecycleStatusCode: string;
        durationMs: number;
        fileSizeBytes: number;
        sampleRateHz: number;
        channelCount: number;
        bitsPerSample: number;
        mimeType: string;
        verifiedAt: string | null;
        retentionUntil: string | null;
        streamUrl: string;
    };
    candidates: AdminSoundRawAudioCandidate[];
    metric: Record<string, number | string | null> | null;
    currentValidation: AdminSoundRawAudioValidation | null;
};
export type AdminSoundRawAudioValidationRequest = {
    validationResultCode: AdminSoundValidationResultCode;
    userDisplayActionCode: AdminSoundUserDisplayActionCode;
    correctedSoundGroupCode?: string | null;
    correctedDisplayLabelCode?: string | null;
    selectedCandidateId?: number | null;
    validationReason: string;
};
const toQueryString = (params?: Record<string, string | number | undefined>) => {
    if (!params)
        return "";
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value != null && value !== "") {
            searchParams.set(key, String(value));
        }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : "";
};
export const fetchSoundAnalysisResults = async (params?: SoundAnalysisQuery) => {
    const data = await request<PagedResponse<SoundAnalysisV2ListItem>>(`/api/v2/admin/sound-analysis-events${toQueryString({
        from: params?.startDate,
        to: params?.endDate,
        subjectKey: params?.subjectKey,
        eventStatusCode: params?.eventStatusCode,
        page: params?.page,
        size: params?.size,
    })}`);
    return data.items;
};
export const fetchSoundAnalysisResultPage = async (params?: SoundAnalysisQuery): Promise<PagedResponse<SoundAnalysisV2ListItem>> => {
    const data = await request<PagedResponse<SoundAnalysisV2ListItem>>(`/api/v2/admin/sound-analysis-events${toQueryString({
        from: params?.startDate,
        to: params?.endDate,
        subjectKey: params?.subjectKey,
        eventStatusCode: params?.eventStatusCode,
        page: params?.page,
        size: params?.size,
    })}`);
    return data;
};
export const fetchSoundAnalysisDetail = async (resultId: number) => {
    return request<SoundAnalysisV2Detail>(`/api/v2/admin/sound-analysis-events/${resultId}`);
};
export const fetchAdminEafrEvents = async (params?: AdminEafrEventQuery) =>
    request<PagedResponse<AdminEafrEventListItem>>(`/api/v3/admin/sound-analysis-events${toQueryString(params)}`);
export const fetchAdminEafrEventDetail = async (eventId: number) =>
    request<AdminEafrEventDetail>(`/api/v3/admin/sound-analysis-events/${eventId}`);
export const fetchSoundRawAudioReviews = async (params?: {
    validationResultCode?: AdminSoundValidationFilter;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
}) => request<PagedResponse<AdminSoundRawAudioReviewItem>>(`/api/v2/admin/sound-raw-audio-reviews${toQueryString(params)}`);
export const fetchSoundRawAudioReviewDetail = async (eventId: number) =>
    request<AdminSoundRawAudioReviewDetail>(`/api/v2/admin/sound-raw-audio-reviews/${eventId}`);
export const fetchSoundRawAudioValidationHistory = async (eventId: number) =>
    request<AdminSoundRawAudioValidationHistory[]>(`/api/v2/admin/sound-raw-audio-reviews/${eventId}/validation-history`);
export const saveSoundRawAudioValidation = async (eventId: number, payload: AdminSoundRawAudioValidationRequest) =>
    request<AdminSoundRawAudioValidation>(`/api/v2/admin/sound-raw-audio-reviews/${eventId}/validation`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
export const fetchSoundRawAudioBlob = async (eventId: number) => {
    let token = getStoredAccessToken();
    if (!token) {
        clearSession();
        redirectToAdminLogin();
        throw new ApiError(getStatusFallbackMessage(401), 401, "AUTH_REQUIRED");
    }
    const send = (accessToken: string) => fetch(`${API_BASE_URL}/api/v2/admin/sound-analysis-events/${eventId}/raw-audio`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    let response = await send(token);
    if (response.status === 401) {
        try {
            token = await refreshAccessToken();
            response = await send(token);
        } catch (error) {
            clearSession();
            redirectToAdminLogin();
            throw error;
        }
    }
    if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as ApiEnvelope<unknown> | null;
        const fallbackMessage = getStatusFallbackMessage(response.status);
        throw new ApiError(isUnreadableMessage(payload?.message) ? fallbackMessage : payload?.message ?? fallbackMessage, response.status, payload?.code);
    }
    return response.blob();
};

export type AdminSoundDatasetReviewStatus = "PENDING" | "APPROVED" | "REJECTED";
export type AdminSoundDatasetReviewFilter = AdminSoundDatasetReviewStatus | "TRAINED";
export type AdminMutationReasonCode = "OPERATOR_REQUEST" | "DATA_CORRECTION" | "SECURITY_RESPONSE" | "TEST_ROLLBACK" | "OTHER";
export type AdminSoundDatasetSampleItem = {
    soundDatasetSampleId: number;
    collectorName: string;
    userTagCode: string | null;
    userTagName: string | null;
    adminTagCode: string | null;
    adminTagName: string | null;
    reviewStatus: AdminSoundDatasetReviewFilter;
    memo: string | null;
    appVersion: string | null;
    deviceModel: string | null;
    modelName: string | null;
    modelVersion: string | null;
    modelLabelVersion: string | null;
    modelFileChecksum: string | null;
    mappingStatus: string;
    trainingStatus: string;
    filePath: string;
    durationSeconds: number | null;
    registeredAt: string;
    reviewedAt: string | null;
};

export type AdminSoundDatasetSampleDetail = Omit<AdminSoundDatasetSampleItem, "filePath" | "durationSeconds"> & {
    reviewerAccountId: number | null;
    file: {
        soundDatasetFileId: number;
        originalFileName: string;
        storedFileName: string;
        filePath: string;
        fileSize: number;
        mimeType: string;
        sampleRate: number | null;
        channelCount: number | null;
        durationSeconds: number | null;
        checksum: string | null;
    };
    audioMetrics: Record<string, number | null> | null;
    yamnetResults: Array<{
        soundDatasetYamnetResultId: number;
        rankNo: number;
        soundModelLabelId: number | null;
        labelCode: string;
        modelLabel: string;
        modelLabelKo: string | null;
        score: number;
    }>;
    updatedAt: string;
};

export const fetchSoundDatasetSamples = async (reviewStatus?: AdminSoundDatasetReviewFilter) => {
    const data = await request<{ items: AdminSoundDatasetSampleItem[] }>(`/api/v1/admin/sound-dataset-samples${toQueryString({ reviewStatus })}`);
    return data.items;
};

export const fetchSoundDatasetSampleDetail = async (sampleId: number) =>
    request<AdminSoundDatasetSampleDetail>(`/api/v1/admin/sound-dataset-samples/${sampleId}`);

export type AdminSoundDatasetReviewRequest = {
    reviewStatus: AdminSoundDatasetReviewStatus;
    adminTagCode?: string;
    adminTagName?: string;
    memo?: string;
    reasonCode: AdminMutationReasonCode;
    reason: string;
};

export const reviewSoundDatasetSample = async (sampleId: number, payload: AdminSoundDatasetReviewRequest) =>
    request<{ sample: AdminSoundDatasetSampleDetail; audit: AdminMutationAuditResponse }>(
        `/api/v1/admin/sound-dataset-samples/${sampleId}/review`,
        { method: "PATCH", body: JSON.stringify(payload) },
    );

export const fetchSoundDatasetAudioBlob = async (sampleId: number) => {
    const send = (accessToken: string) => fetch(`${API_BASE_URL}/api/v1/admin/sound-dataset-samples/${sampleId}/audio`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    let token = getStoredAccessToken();
    if (!token) {
        redirectToAdminLogin();
        throw new ApiError("관리자 로그인이 필요합니다.", 401);
    }
    let response = await send(token);
    if (response.status === 401) {
        token = await refreshAccessToken();
        response = await send(token);
    }
    if (!response.ok) {
        throw new ApiError("데이터셋 원음을 불러오지 못했습니다.", response.status);
    }
    return response.blob();
};
export const createSoundAnalysisTuningSample = async (
    soundAnalysisEventId: number,
    soundAnalysisEventCandidateId: number,
    payload: SoundAnalysisTuningSampleCreateRequest,
) => {
    return request<SoundAnalysisTuningSample>(
        `/api/v2/admin/sound-analysis-events/${soundAnalysisEventId}/candidates/${soundAnalysisEventCandidateId}/tuning-samples`,
        {
            method: "POST",
            body: JSON.stringify(payload),
        },
    );
};
export const fetchAdminSoundStatisticsSummary = async (params?: AdminSoundStatisticsQuery) => {
    return request<AdminSoundStatisticsSummary>(`/api/v2/admin/sound-statistics/summary${toQueryString(params)}`);
};
export const fetchAdminSoundLabelStatistics = async (params?: AdminSoundStatisticsQuery) => {
    return request<AdminSoundLabelStatistics[]>(`/api/v2/admin/sound-statistics/labels${toQueryString(params)}`);
};
export const fetchAdminSoundConfidenceDistribution = async (params?: AdminSoundStatisticsQuery) => {
    return request<AdminSoundConfidenceBin[]>(`/api/v2/admin/sound-statistics/confidence-distribution${toQueryString(params)}`);
};
export const fetchAdminSoundPolicy = async (policyVersion: string) => {
    return request<AdminSoundThresholdPolicy>(`/api/v2/admin/sound-policies/${encodeURIComponent(policyVersion)}`);
};
export const simulateAdminSoundThreshold = async (payload: AdminSoundThresholdSimulationRequest) => {
    return request<AdminSoundThresholdSimulationResult>("/api/v2/admin/sound-statistics/threshold-simulation", {
        method: "POST",
        body: JSON.stringify(payload),
    });
};
export const fetchCommonCodes = async (groupCode: string) => {
    return request<CommonCodeOption[]>(`/api/v1/admin/common-codes?groupCode=${encodeURIComponent(groupCode)}`);
};
export const fetchSoundModelReleases = async () => {
    const data = await request<{
        items: SoundModelRelease[];
    }>("/api/v1/admin/sound-model/releases");
    return data.items;
};
export const createSoundModelRelease = async (payload: SoundModelReleaseSaveRequest, modelFile: File): Promise<SoundModelReleaseMutationResponse> => {
    let token = getStoredAccessToken();
    if (!token) {
        clearSession();
        redirectToAdminLogin();
        throw new ApiError(getStatusFallbackMessage(401), 401, "AUTH_REQUIRED");
    }

    const formData = new FormData();
    formData.append("modelFile", modelFile);
    formData.append("metadata", new Blob([JSON.stringify(payload)], { type: "application/json" }));

    const send = async (accessToken: string) => {
        const response = await fetch(`${API_BASE_URL}/api/v1/admin/sound-model/releases`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        });
        const result = (await response.json().catch(() => null)) as ApiEnvelope<SoundModelRelease> | null;
        return { response, result };
    };

    let { response, result } = await send(token);
    if (response.status === 401) {
        try {
            token = await refreshAccessToken();
            ({ response, result } = await send(token));
        } catch (error) {
            clearSession();
            redirectToAdminLogin();
            throw error;
        }
    }
    if (!response.ok || !result?.success) {
        if (response.status === 401) {
            clearSession();
            redirectToAdminLogin();
        }
        const fallbackMessage = getStatusFallbackMessage(response.status);
        const message = isUnreadableMessage(result?.message) ? fallbackMessage : result?.message ?? fallbackMessage;
        throw new ApiError(message, response.status, result?.code);
    }
    return result.data;
};
export const updateSoundModelRelease = async (releaseId: number, payload: SoundModelReleaseSaveRequest) => {
    return request<SoundModelReleaseMutationResponse>(`/api/v1/admin/sound-model/releases/${releaseId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
    });
};
export const deleteSoundModelRelease = async (releaseId: number, payload: AdminDestructiveMutationRequest) => {
    return request<AdminDestructiveMutationResponse>(`/api/v1/admin/sound-model/releases/${releaseId}/delete`, {
        method: "POST",
        body: JSON.stringify(payload),
    });
};
export const fetchSoundModelLabels = async (params?: {
    modelName?: string;
    modelVersion?: string;
    useYn?: string;
    keyword?: string;
}) => {
    const data = await request<{
        items: SoundModelLabel[];
    }>(`/api/v1/admin/sound-models/labels${toQueryString(params)}`);
    return data.items;
};
export const fetchSoundModelMappingRules = async (params?: {
    modelName?: string;
    modelVersion?: string;
    labelCode?: string;
    resultSoundType?: string;
    resultSoundSubType?: string;
    mappingStatus?: string;
    useYn?: string;
}) => {
    const data = await request<{
        items: SoundModelMappingRule[];
    }>(`/api/v1/admin/sound-models/mapping-rules${toQueryString(params)}`);
    return data.items;
};
export const fetchAdminLifeSignalSummary = async () => {
    return request<AdminLifeSignalSummary>("/api/v1/admin/life-signals/summary");
};

type AdminLifeSignalApiItem = AdminLifeSignalItem;
export const fetchSoundModelReleaseDeletionPreview = async (releaseId: number) =>
    request<AdminMutationImpactPreview>(`/api/v1/admin/sound-model/releases/${releaseId}/deletion-preview`);

type AdminLifeSignalApiDetail = AdminLifeSignalDetail;

const mapAdminLifeSignalItem = (item: AdminLifeSignalApiItem): AdminLifeSignalItem => ({
    ...item,
    seniorUserAccountId: item.seniorUserAccountId,
    elderlyName: item.elderlyName,
    latestScheduleResponseStatus: item.latestScheduleResponseStatus ?? null,
    latestScheduleResponseAt: item.latestScheduleResponseAt ?? null,
    latestSoundStatus: item.latestSoundStatus ?? null,
    latestSoundAt: item.latestSoundAt ?? null,
    latestDeviceStatus: item.latestDeviceStatus ?? null,
    latestDeviceStatusAt: item.latestDeviceStatusAt ?? null,
    latestEmergencyEventStatus: item.latestEmergencyEventStatus ?? null,
    needsAttention: item.needsAttention,
});

const mapAdminLifeSignalDetail = (item: AdminLifeSignalApiDetail): AdminLifeSignalDetail => ({
    ...item,
    seniorUserAccountId: item.seniorUserAccountId,
    elderlyName: item.elderlyName,
    latestScheduleResponse: item.latestScheduleResponse ?? null,
    latestSound: item.latestSound ?? null,
    latestDeviceStatus: item.latestDeviceStatus ?? null,
    latestEmergencyEvent: item.latestEmergencyEvent ?? null,
    timeline: item.timeline,
});

export const fetchAdminLifeSignals = async () => {
    const data = await request<{
        items: AdminLifeSignalApiItem[];
    }>("/api/v1/admin/life-signals");
    return data.items.map(mapAdminLifeSignalItem);
};
export const fetchAdminLifeSignalDetail = async (seniorUserAccountId: number) => {
    const data = await request<AdminLifeSignalApiDetail>(`/api/v1/admin/senior-accounts/${seniorUserAccountId}/life-signals`);
    return mapAdminLifeSignalDetail(data);
};
export const fetchAdminCustomerInquiries = async (params?: {
    status?: string;
    keyword?: string;
}) => {
    type AdminCustomerInquiryApiItem = AdminCustomerInquiryItem & {
        memberId: number | null;
    };
    const data = await request<{
        items: AdminCustomerInquiryApiItem[];
    }>(`/api/v1/admin/customer-inquiries${toQueryString(params)}`);
    return data.items.map(({ memberId: _memberId, ...item }) => item);
};
export const fetchAdminCustomerInquiryDetail = async (inquiryId: number) => {
    type AdminCustomerInquiryApiDetail = AdminCustomerInquiryDetail & {
        memberId: number | null;
    };
    const { memberId: _memberId, ...detail } = await request<AdminCustomerInquiryApiDetail>(`/api/v1/admin/customer-inquiries/${inquiryId}`);
    return detail;
};
export const answerAdminCustomerInquiry = async (inquiryId: number, answerContent: string, reasonCode: string, reason: string) => {
    return request<{ inquiry: AdminCustomerInquiryDetail; audit: AdminMutationAuditResponse }>(`/api/v1/admin/customer-inquiries/${inquiryId}/answer`, {
        method: "POST",
        body: JSON.stringify({ answerContent, reasonCode, reason }),
    });
};
export const updateAdminCustomerInquiryStatus = async (inquiryId: number, status: string, reasonCode: string, reason: string) => {
    return request<{ inquiry: AdminCustomerInquiryDetail; audit: AdminMutationAuditResponse }>(`/api/v1/admin/customer-inquiries/${inquiryId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status, reasonCode, reason }),
    });
};
export const fetchAdminTermsPolicies = async (params?: {
    keyword?: string;
    categoryCode?: string;
    active?: string;
}) => {
    const data = await request<{
        items: AdminTermsPolicyItem[];
    }>(`/api/v1/admin/terms/policies${toQueryString(params)}`);
    return data.items;
};
export const createAdminTermsPolicy = async (payload: AdminTermsPolicySaveRequest) => {
    return request<AdminTermsPolicyItem>("/api/v1/admin/terms/policies", {
        method: "POST",
        body: JSON.stringify(payload),
    });
};
export const fetchAdminTermsPolicy = async (termsPolicyId: number) =>
    request<AdminTermsPolicyDetail>(`/api/v1/admin/terms/policies/${termsPolicyId}`);
export const updateAdminTermsPolicy = async (termsPolicyId: number, payload: AdminTermsPolicySaveRequest) => {
    return request<AdminTermsPolicyItem>(`/api/v1/admin/terms/policies/${termsPolicyId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
    });
};
export type AdminTermsPublishPreview = {
    executable: boolean;
    currentStatus: string;
    activeScopeCount: number;
    confirmationText: string;
};
export const fetchAdminTermsPublishPreview = async (termsPolicyId: number) =>
    request<AdminTermsPublishPreview>(`/api/v1/admin/terms/policies/${termsPolicyId}/publish-preview`);
export const publishAdminTermsPolicy = async (termsPolicyId: number, payload: {
    reasonCode: string;
    reason: string;
    confirmationText: string;
}) => request<{ policy: AdminTermsPolicyItem; audit: AdminMutationAuditResponse }>(
    `/api/v1/admin/terms/policies/${termsPolicyId}/publish`,
    { method: "POST", body: JSON.stringify(payload) },
);
const publicTermsQuery = (params?: { userType?: string; version?: string }) => toQueryString(params);
export const fetchPublicTerms = async (userType?: string) =>
    request<PublicTermsListItem[]>(`/api/v1/public/terms${publicTermsQuery({ userType })}`, { auth: false });
export const fetchPublicTermsDetail = async (termsTypeCode: string, params?: { userType?: string; version?: string }) =>
    request<PublicTermsDetail>(`/api/v1/public/terms/${encodeURIComponent(termsTypeCode)}${publicTermsQuery(params)}`, { auth: false });
export type AdminConnectionConsentTransitionPreview = {
    reconsentRequired: boolean;
    activeConnectionCount: number;
    executable: boolean;
    confirmationText: string;
};
export const fetchConnectionConsentTransitionPreview = async () =>
    request<AdminConnectionConsentTransitionPreview>("/api/v1/admin/terms/connection-consents/transition-existing-preview");
export const transitionExistingConnectionConsents = async (payload: {
    reasonCode: string;
    reason: string;
    confirmationText: string;
}) => request<{ reconsentRequired: boolean; transitionedCount: number; audit: AdminMutationAuditResponse }>(
    "/api/v1/admin/terms/connection-consents/transition-existing",
    { method: "POST", body: JSON.stringify(payload) },
);
export type AdminSoundSubjectReconcileItem = {
    userAccountId: number;
    disposition: string;
    soundDataSubjectId: number | null;
    missingConditionCodes: string[];
    errorCode: string | null;
    audit: AdminMutationAuditResponse | null;
};
export const reconcileMissingSoundSubjects = async (payload: {
    userAccountIds: number[];
    reasonCode: string;
    reason: string;
    confirmationText: string;
}) => request<{ items: AdminSoundSubjectReconcileItem[] }>(
    "/api/v2/admin/sound-subjects/reconcile-missing",
    { method: "POST", body: JSON.stringify(payload) },
);
export type AdminCollectionScheduleRule = {
    schedulePolicyRuleId: number;
    ruleSequenceNo: number;
    targetCode: string;
    executionModeCode: string;
    allDay: boolean;
    startTime: string | null;
    endTime: string | null;
    repeatDaysCode: string;
    intervalMinutes: number;
    priorityNo: number;
    soundCaptureDurationSec: number | null;
    locationAcquisitionWindowSec: number | null;
    locationAcceptedAccuracyMeter: number | null;
    slotLatenessTargetSec: number;
    slotLatenessLimitSec: number;
};
export type AdminCollectionSchedulePolicy = {
    schedulePolicyId: number;
    schedulePolicyVersion: number;
    status: string;
    effectiveAt: string;
    timeZone: string;
    checksum: string;
    changeReason: string;
    rules: AdminCollectionScheduleRule[];
};
export const fetchAdminCollectionSchedulePolicyCurrent = async () =>
    request<{ active: AdminCollectionSchedulePolicy | null; scheduled: AdminCollectionSchedulePolicy | null }>(
        "/api/v3/admin/collection-schedule-policies/current");
export const fetchAdminCollectionSchedulePolicyHistory = async () =>
    request<AdminCollectionSchedulePolicy[]>("/api/v3/admin/collection-schedule-policies");
export const cancelAdminCollectionSchedulePolicy = async (policyVersion: number, payload: {
    reasonCode: string;
    reason: string;
    confirmationText: string;
}) => request<{ policy: AdminCollectionSchedulePolicy; audit: AdminMutationAuditResponse }>(
    `/api/v3/admin/collection-schedule-policies/${policyVersion}/cancel`,
    { method: "POST", body: JSON.stringify(payload) },
);
export const createAdminCollectionSchedulePolicy = async (payload: {
    policy: {
        expectedActivePolicyVersion: number;
        expectedScheduledPolicyVersion?: number;
        effectiveAt: string;
        timeZone: string;
        changeReason: string;
        rules: Array<Omit<AdminCollectionScheduleRule, "schedulePolicyRuleId">>;
    };
    reasonCode: string;
    reason: string;
    confirmationText: string;
}) => request<{ policy: AdminCollectionSchedulePolicy; audit: AdminMutationAuditResponse }>(
    "/api/v3/admin/collection-schedule-policies",
    { method: "POST", body: JSON.stringify(payload) },
);

export type AccountDeletionRequestItem = {
    requestId: number; appType: string; requestType: string; loginProvider: string;
    contactEmail: string | null; contactPhone: string | null; requestReason: string | null;
    status: string; userAccountId: number | null; verificationMethod: string | null;
    verifiedAt: string | null; completedAt: string | null; createdAt: string;
    history: Array<{ beforeStatus: string | null; afterStatus: string; actorType: string; reason: string | null; eventAt: string }>;
};
export const fetchAccountDeletionRequest = async (id: number) => request<AccountDeletionRequestItem>(`/api/v1/admin/account-deletion-requests/${id}`);
export const fetchAccountDeletionRequests = async (status?: string) => request<AccountDeletionRequestItem[]>(`/api/v1/admin/account-deletion-requests${status ? `?status=${encodeURIComponent(status)}` : ""}`);
export const verifyAccountDeletionRequest = async (id: number, userAccountId: number, reason: string, verificationMethod: string) => request<AccountDeletionRequestItem>(`/api/v1/admin/account-deletion-requests/${id}/verify`, { method: "POST", body: JSON.stringify({ userAccountId, verificationMethod, reason }) });
export const modifyAccountDeletionRequestStatus = async (id: number, status: string, reason: string) => request<AccountDeletionRequestItem>(`/api/v1/admin/account-deletion-requests/${id}/modify-status`, { method: "POST", body: JSON.stringify({ status, reason }) });
export const completeAccountDeletionRequest = async (id: number, reason: string, confirmationText: string) => request<AccountDeletionRequestItem>(`/api/v1/admin/account-deletion-requests/${id}/complete`, { method: "POST", body: JSON.stringify({ reason, confirmationText }) });
export const completePartialDataDeletionRequest = async (id: number, reason: string, confirmationText: string) => request<AccountDeletionRequestItem>(`/api/v1/admin/account-deletion-requests/${id}/complete-partial-data`, { method: "POST", body: JSON.stringify({ reason, confirmationText }) });
